import { JobEntity, JobOperation } from '@commenda/api';
import {
  SalesTaxOverviewData,
  SalesTaxTransactionsActivityType,
} from '@commenda/api/sales-tax/types';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import {
  Button,
  Dropdown,
  IconButton,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { useRouter } from 'next/router';
import { JobsLogTable, useIndirectTaxJobWatcher } from '../common/jobs';
import StatsOverview from '../common/overview';
import SalesTaxSectionLayout from '../common/section-layout';
import { getTransactionOverviewStatsColor } from '../utils';

interface SalesTaxTransactionOverviewSectionProps {
  overviewData: SalesTaxOverviewData<SalesTaxTransactionsActivityType>[];
  handleAddTransactions: () => void;
  corporationId: number;
  openIntegrationModal: () => void;
  shouldShowFailedTransactions: boolean;
}

const SalesTaxTransactionOverviewSection = ({
  overviewData,
  handleAddTransactions,
  corporationId,
  openIntegrationModal,
  shouldShowFailedTransactions,
}: SalesTaxTransactionOverviewSectionProps) => {
  const router = useRouter();

  const { getJobsByEntityAndOperation: getImportJobs } =
    useIndirectTaxJobWatcher({
    corporation_id: corporationId,
    entity: JobEntity.INVOICE,
    operation: JobOperation.IMPORT,
  });

  const { getJobsByEntityAndOperation: getExportJobs } =
    useIndirectTaxJobWatcher({
    corporation_id: corporationId,
    entity: JobEntity.INVOICE,
    operation: JobOperation.EXPORT,
  });

  const importJobTableVisibility = useToggle();
  const exportJobTableVisibility = useToggle();

  const isRootfiIntegrationEnabled = isFeatureLaunched(
    FeatureLaunch.SALES_TAX_ROOTFI_INTEGRATION
  );

  return (
    <SalesTaxSectionLayout
      renderHeader={() => {
        return (
          <div className="flex items-center justify-between w-full">
            <span />

            <div className="flex items-center gap-2">
              <Dropdown
                menu={{
                  groups: [
                    {
                      items: [
                        {
                          icon: Icons.Download01,
                          label: 'View import logs',
                          onClick: importJobTableVisibility.show,
                        },
                        {
                          icon: Icons.Upload01,
                          label: 'View export logs',
                          onClick: exportJobTableVisibility.show,
                        },
                      ],
                    },
                  ],
                }}
                align="end"
              >
                <Button variant="tertiary" size="sm">
                  View transaction reports
                </Button>
              </Dropdown>

              <div className="flex items-center gap-md">
                {isRootfiIntegrationEnabled ? (
                  <Dropdown
                    menu={{
                      groups: [
                        {
                          items: [
                            {
                              icon: Icons.Link01,
                              label: 'Connect integration',
                              onClick: openIntegrationModal,
                            },
                            {
                              icon: Icons.Download01,
                              label: 'Import transactions',
                              onClick: handleAddTransactions,
                            },
                          ],
                        },
                      ],
                    }}
                    align="end"
                  >
                    <Button variant="secondary" size="sm">
                      <Icons.Plus className="size-4" />
                      <span>Add transaction</span>
                    </Button>
                  </Dropdown>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleAddTransactions}
                  >
                    <Icons.Download01 className="size-4" />
                    <span>Import transactions</span>
                  </Button>
                )}
                {shouldShowFailedTransactions && (
                  <Dropdown
                    align="end"
                    alignOffset={8}
                    sideOffset={8}
                    menu={{
                      groups: [
                        {
                          items: [
                            {
                              label: 'View failed records',
                              onClick: () => {
                                router.push(
                                  `/sales-tax/address-validation?corporationId=${corporationId}`
                                );
                              },
                            },
                          ],
                        },
                      ],
                    }}
                  >
                    <IconButton
                      aria-label="View failed records"
                      icon={<Icons.DotsVertical className="size-4" />}
                      size="lg"
                    />
                  </Dropdown>
                )}
              </div>
            </div>
          </div>
        );
      }}
      _className="px-0"
    >
      <StatsOverview
        items={overviewData?.map((each) => {
          if (
            each.type === SalesTaxTransactionsActivityType.TOTAL_TRANSACTIONS
          ) {
            return {
              type: each.type,
              title: each.activityTitle,
              value: each.value,
            };
          }
          return {
            type: each.type,
            title: each.activityTitle,
            value: each.value,
            subTitle: '$',
            subTitlePosition: 'before',
          };
        })}
        getTextColorClass={getTransactionOverviewStatsColor}
      />

      <JobsLogTable
        isOpen={importJobTableVisibility.visible}
        onClose={importJobTableVisibility.hide}
        data={getImportJobs(JobEntity.INVOICE, JobOperation.IMPORT)}
        additionalColumns={['user']}
        corporationId={corporationId}
      />

      <JobsLogTable
        isOpen={exportJobTableVisibility.visible}
        onClose={exportJobTableVisibility.hide}
        jobOperation={JobOperation.EXPORT}
        data={getExportJobs(JobEntity.INVOICE, JobOperation.EXPORT)}
        additionalColumns={['user']}
        corporationId={corporationId}
      />
    </SalesTaxSectionLayout>
  );
};

export default SalesTaxTransactionOverviewSection;
